import { useEffect, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Analytics } from '@trello/atlassian-analytics';

import dragAndDropImage from './drag-and-drop.png';
import panelsImage from './panels.png';
import plannerImage from './planner.png';
import quickCaptureImage from './quick-capture.png';

import styles from './PPOnboardingModalScreenFallback.less';

const images = [quickCaptureImage, plannerImage, dragAndDropImage, panelsImage];
const timers = [6000, 6000, 5000, 8000];

interface PPOnboardingModalScreenFallbackProps {
  activeScreen: number;
  onAnimationComplete: () => void;
  onOnboardingComplete: () => void;
}

export const PPOnboardingModalScreenFallback: FunctionComponent<
  PPOnboardingModalScreenFallbackProps
> = ({ activeScreen, onAnimationComplete, onOnboardingComplete }) => {
  const intl = useIntl();
  const headings = [
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.capture-everything',
      defaultMessage: 'Capture everything with Inbox',
      description: 'Heading for onboarding screen describing use of Inbox',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.schedule-with-planner',
      defaultMessage: 'Schedule your to-dos with Planner',
      description: 'Heading for onboarding screen describing use of Planner',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.drag-drop-get-it-done',
      defaultMessage: 'Drag, drop, get it done',
      description:
        'Heading for onboarding screen describing drag and drop functionality',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.show-or-hide-panels',
      defaultMessage: 'Show or hide panels',
      description: 'Heading for onboarding screen describing panel navigation',
    }),
  ];
  const subheadings = [
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.a-private-space',
      defaultMessage:
        'A private space to capture to-dos, notes, and reminders from anywhere. Nothing slips through the cracks.',
      description:
        'Subheading for onboarding screen describing usefulness of Inbox',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.view-your-calendar',
      defaultMessage:
        'View your calendar alongside your to-dos, create focused time slots, and meet deadlines with ease.',
      description: 'Subheading for onboarding screen describing use of Planner',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.keep-everything-in-view',
      defaultMessage:
        'Keep everything in view — organize, schedule, and move cards effortlessly between your boards, Inbox, and Planner.',
      description:
        'Subheading for onboarding screen describing drag and drop functionality',
    }),
    intl.formatMessage({
      id: 'templates.split_screen.pp-beta-onboarding.view-your-boards',
      defaultMessage:
        'View your boards, Inbox, and Planner side-by-side, or on its own. Show only the feature you need, when you need it.',
      description:
        'Subheading for onboarding screen describing panel navigation',
    }),
  ];

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentType: 'overlay',
      componentName: 'ppOnboardingModalScreenFallback',
      source: 'boardScreen',
      attributes: {
        screen: activeScreen,
      },
    });
    const timer = setTimeout(onAnimationComplete, timers[activeScreen]);
    return () => clearTimeout(timer);
  }, [activeScreen, onAnimationComplete]);

  return (
    <div className={styles.screen}>
      <h1 className={styles.heading}>{headings[activeScreen]}</h1>
      <h2 className={styles.subheading}>{subheadings[activeScreen]}</h2>
      {activeScreen === 3 && (
        <Button
          appearance="discovery"
          iconAfter={ArrowRightIcon}
          onClick={onOnboardingComplete}
        >
          <FormattedMessage
            id="templates.split_screen.pp-beta-onboarding.continue"
            defaultMessage="Reveal the new Trello"
            description="Beta onboarding modal continue button"
          />
        </Button>
      )}
      <div className={styles.lottieFallback}>
        <img alt={subheadings[activeScreen]} src={images[activeScreen]} />
      </div>
    </div>
  );
};
